import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Plus, Trash2, Copy } from 'lucide-react';
import { GST_RATES } from '../../lib/states';
import { formatINR, calculateLineItem } from '../../lib/gst';
import { useLanguage } from '../../contexts/LanguageContext';

const COLS = '20px minmax(220px,2.2fr) 92px 58px 76px 64px 76px 88px 32px 32px';

export default function LineItems({ lineItems, addLineItem, addMultipleLineItems, removeLineItem, removeLineItems, duplicateLineItem, updateLineItem, savedItems = [] }) {
  const { t } = useLanguage();
  const [selected, setSelected] = useState(new Set());
  const headers = ['', t('description'), 'HSN/SAC', t('qty'), t('rate'), t('disc'), t('gst'), t('amount'), '', ''];
  const descriptionRequired = lineItems.every((item) => !String(item.description || '').trim());
  const requiredDescriptionClass = (value, extra = '') => [
    extra,
    descriptionRequired && !String(value || '').trim() ? 'border-red-200 bg-red-50/30 focus-visible:ring-red-200' : '',
  ].filter(Boolean).join(' ');
  const updateDescription = (id, value) => {
    const saved = savedItems.find((item) => String(item.description || '').trim().toLowerCase() === value.trim().toLowerCase());
    if (saved) {
      updateLineItem(id, {
        description: value,
        hsn: saved.hsn || saved.hsnSac || '',
        unit: saved.unit || 'Nos',
        rate: Number(saved.rate || saved.salePrice || 0),
        purchasePrice: Number(saved.purchasePrice || 0),
        gstRate: Number(saved.gstRate || 18),
      });
      return;
    }
    updateLineItem(id, { description: value });
  };

  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (selected.size === lineItems.length) setSelected(new Set());
    else setSelected(new Set(lineItems.map((l) => l.id)));
  };

  const deleteSelected = () => {
    if (lineItems.length - selected.size < 1) return;
    removeLineItems(selected);
    setSelected(new Set());
  };

  const allChecked = lineItems.length > 0 && selected.size === lineItems.length;
  const someChecked = selected.size > 0;

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 shadow-sm" data-testid="line-items-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold text-slate-900">{t('itemsServices')}</h3>
        <div className="flex items-center gap-2">
          {someChecked && (
            <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={deleteSelected}>
              <Trash2 className="w-3 h-3 mr-1" />
              {t('delete')} {selected.size}
            </Button>
          )}
          <span className="hidden sm:inline text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400">{t('lineItems')}</span>
        </div>
      </div>

      {/* ── DESKTOP: grid table ── */}
      <div className="hidden lg:block overflow-x-auto">
        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: '0 6px', alignItems: 'center' }}
          className="text-[10px] uppercase tracking-wider font-bold text-slate-400 border-b border-slate-200 pb-2 mb-1">
          <input type="checkbox" checked={allChecked} onChange={toggleAll} className="w-3.5 h-3.5 accent-slate-900 cursor-pointer" />
          {headers.slice(1).map((h, i) => (
            <div key={i} style={{ textAlign: i >= 1 && i <= 6 ? 'center' : 'left' }}>{h}</div>
          ))}
        </div>

        {lineItems.map((li, idx) => {
          const { taxableAmount, gstAmount } = calculateLineItem(li);
          const total = taxableAmount + gstAmount;
          const isSelected = selected.has(li.id);
          return (
            <div key={li.id}
              style={{ display: 'grid', gridTemplateColumns: COLS, gap: '0 6px', alignItems: 'center' }}
              className={`py-1.5 border-b border-slate-100 last:border-0 transition-colors ${isSelected ? 'bg-slate-50' : ''}`}
              data-testid={`line-item-row-${idx}`}
            >
              <input type="checkbox" checked={isSelected} onChange={() => toggleRow(li.id)} className="w-3.5 h-3.5 accent-slate-900 cursor-pointer" />
              <Input value={li.description || ''} onChange={(e) => updateDescription(li.id, e.target.value)} placeholder={t('description')} list="items-datalist" className={requiredDescriptionClass(li.description)} data-testid={`li-desc-desktop-${idx}`} />
              <Input value={li.hsn} onChange={(e) => updateLineItem(li.id, { hsn: e.target.value })} placeholder="998314" className="font-mono text-xs text-center" data-testid={`li-hsn-${idx}`} />
              <Input type="number" step="0.01" min="0" value={li.qty} onChange={(e) => updateLineItem(li.id, { qty: parseFloat(e.target.value) || 0 })} className="tabular-nums text-center" data-testid={`li-qty-${idx}`} />
              <Input type="number" step="0.01" min="0" value={li.rate} onChange={(e) => updateLineItem(li.id, { rate: parseFloat(e.target.value) || 0 })} className="tabular-nums text-center" data-testid={`li-rate-${idx}`} />
              <Input type="number" step="0.01" min="0" max="100" value={li.discount} onChange={(e) => updateLineItem(li.id, { discount: parseFloat(e.target.value) || 0 })} className="tabular-nums text-center" data-testid={`li-discount-${idx}`} />
              <Select value={String(li.gstRate)} onValueChange={(v) => updateLineItem(li.id, { gstRate: parseFloat(v) })}>
                <SelectTrigger className="w-full justify-center" data-testid={`li-gst-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>{GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
              </Select>
              <div className="text-sm font-mono tabular-nums font-semibold text-slate-900 whitespace-nowrap text-center" data-testid={`li-amount-${idx}`}>{formatINR(total)}</div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => duplicateLineItem(li.id)} title={t('duplicate')}><Copy className="w-3.5 h-3.5 text-slate-400 hover:text-slate-700" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { if (lineItems.length > 1) removeLineItem(li.id); }} disabled={lineItems.length === 1} title={t('remove')} data-testid={`li-remove-${idx}`}><Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" /></Button>
            </div>
          );
        })}
        <datalist id="items-datalist">{savedItems.map((it) => <option key={it.id} value={it.description} />)}</datalist>
      </div>

      {/* ── MOBILE: card per row ── */}
      <div className="lg:hidden space-y-3">
        {lineItems.map((li, idx) => {
          const { taxableAmount, gstAmount } = calculateLineItem(li);
          const total = taxableAmount + gstAmount;
          const isSelected = selected.has(li.id);
          return (
            <div key={li.id}
              className={`border rounded-xl p-3 space-y-2.5 transition-colors ${isSelected ? 'border-slate-400 bg-slate-50' : 'border-slate-200 bg-white'}`}
              data-testid={`line-item-row-${idx}`}
            >
              {/* Description row: checkbox + input + delete */}
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={isSelected} onChange={() => toggleRow(li.id)} className="w-4 h-4 accent-slate-900 cursor-pointer flex-shrink-0" />
                <Input value={li.description || ''} onChange={(e) => updateDescription(li.id, e.target.value)} placeholder={t('description')} list="items-datalist-mobile" className={requiredDescriptionClass(li.description, 'flex-1 font-medium')} data-testid={`li-desc-mobile-${idx}`} />
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => { if (lineItems.length > 1) removeLineItem(li.id); }} disabled={lineItems.length === 1} data-testid={`li-remove-${idx}`}>
                  <Trash2 className="w-4 h-4 text-slate-400" />
                </Button>
              </div>

              {/* 2-col grid: HSN | QTY */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">HSN/SAC</label>
                  <Input value={li.hsn} onChange={(e) => updateLineItem(li.id, { hsn: e.target.value })} placeholder="998314" className="font-mono text-xs text-center mt-0.5" data-testid={`li-hsn-${idx}`} />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{t('qty')}</label>
                  <Input type="number" step="0.01" min="0" value={li.qty} onChange={(e) => updateLineItem(li.id, { qty: parseFloat(e.target.value) || 0 })} className="tabular-nums text-center mt-0.5" data-testid={`li-qty-${idx}`} />
                </div>
              </div>

              {/* 2-col grid: Rate | Disc% */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{t('rate')} (₹)</label>
                  <Input type="number" step="0.01" min="0" value={li.rate} onChange={(e) => updateLineItem(li.id, { rate: parseFloat(e.target.value) || 0 })} className="tabular-nums text-center mt-0.5" data-testid={`li-rate-${idx}`} />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{t('disc')}</label>
                  <Input type="number" step="0.01" min="0" max="100" value={li.discount} onChange={(e) => updateLineItem(li.id, { discount: parseFloat(e.target.value) || 0 })} className="tabular-nums text-center mt-0.5" data-testid={`li-discount-${idx}`} />
                </div>
              </div>

              {/* 2-col grid: GST | Amount + dupe */}
              <div className="grid grid-cols-2 gap-2 items-end">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{t('gst')}</label>
                  <Select value={String(li.gstRate)} onValueChange={(v) => updateLineItem(li.id, { gstRate: parseFloat(v) })}>
                    <SelectTrigger className="mt-0.5" data-testid={`li-gst-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">{t('amount')}</div>
                    <div className="text-base font-bold text-slate-900 tabular-nums" data-testid={`li-amount-${idx}`}>{formatINR(total)}</div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => duplicateLineItem(li.id)} title={t('duplicate')}>
                    <Copy className="w-3.5 h-3.5 text-slate-400" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        <datalist id="items-datalist-mobile">{savedItems.map((it) => <option key={it.id} value={it.description} />)}</datalist>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={addLineItem} data-testid="add-line-item-btn">
          <Plus className="w-4 h-4 mr-1" /> {t('addRow')}
        </Button>
        <Button variant="ghost" size="sm" className="text-slate-500" onClick={() => addMultipleLineItems(5)}>
          <Plus className="w-4 h-4 mr-1" /> {t('addFiveRows')}
        </Button>
      </div>
    </section>
  );
}
